import { ReactNode } from 'react';

import { AnimatedNode } from '@ui/components/Animation';
import { BookmarkButton } from '@ui/components/BookmarkButton';
import { BookmarkButtonBar } from '@ui/components/BookmarkButtonBar';
import { Loader } from '@ui/components/Loader';
import { CameraIcon } from '@ui/icons/CameraIcon';
import { EditIcon } from '@ui/icons/EditIcon';
import { PauseIcon } from '@ui/icons/PauseIcon';
import { StartIcon } from '@ui/icons/StartIcon';

import { MemberClubDto, Tournament } from '@features/shared/api/typings';
import { TournamentSubHeader } from '@features/tournament/ui/components/TournamentSubHeader/TournamentSubHeader';

import styles from './TournamentCardPage.module.css';

export type Props = {
  isPending: boolean;
  club?: MemberClubDto;
  tournament?: Tournament;
  isManager: boolean;
  isAdmin: boolean;
  isPublishable: boolean;
  isPublishing: boolean;
  isPausing: boolean;
  isResuming: boolean;
  noPairModal?: ReactNode;
  video?: ReactNode;
  participants?: ReactNode;
  games?: ReactNode;
  onEdit: () => void;
  onPublish: () => void;
  onPause: () => void;
  onResume: () => void;
  onVideoRooms?: () => void;
  participationButton?: ReactNode;
  isRcfReportAvailable: boolean;
  rcfReportError?: string;
  onRcfReportClick: () => void;
};

export function TournamentCardPage({
  club,
  isPending,
  tournament,
  isManager,
  isAdmin,
  noPairModal,
  video,
  isPublishable,
  isPublishing,
  isPausing,
  isResuming,
  participants,
  games,
  onEdit,
  onPublish,
  onPause,
  onResume,
  onVideoRooms,
  participationButton,
  isRcfReportAvailable,
  rcfReportError,
  onRcfReportClick,
}: Props) {
  return (
    <>
      {!isPending && club && (
        <TournamentSubHeader
          club={club}
          tournament={tournament}
          isManager={isManager}
          isAdmin={isAdmin}
          actions={participationButton}
          isRcfReportAvailable={isRcfReportAvailable}
          rcfReportError={rcfReportError}
          onRcfReportClick={onRcfReportClick}
        />
      )}

      {isPending && <Loader className={styles.loader} centered />}

      {!isPending && <AnimatedNode className={styles.nopair}>{noPairModal}</AnimatedNode>}

      {video && <div className={styles.video}>{video}</div>}

      {participants}

      {games}

      {isManager && (
        <BookmarkButtonBar>
          {tournament?.status !== 'Deleted' && (
            <>
              <BookmarkButton
                icon={<EditIcon />}
                label="Редактировать"
                onClick={onEdit}
                disabled={isPublishing}
              />
            </>
          )}
          {onVideoRooms && (
            <BookmarkButton
              icon={<CameraIcon className={styles.video_icon} />}
              label="Видеокомнаты"
              onClick={onVideoRooms}
            />
          )}
          {tournament?.status === 'Draft' && (
            <BookmarkButton
              icon={<StartIcon />}
              label="Опубликовать"
              onClick={onPublish}
              disabled={!isPublishable || isPublishing}
            />
          )}
          {tournament && tournament?.status === 'InProgress' && (
            <BookmarkButton
              icon={<PauseIcon />}
              label="Приостановить"
              clickedStateBottom={
                <div className={styles.clickable} onClick={onPause}>
                  Да, приостановить
                </div>
              }
              clickedStateTop={<div>Вы уверены?</div>}
              disabled={isPausing}
            />
          )}
          {tournament?.status === 'Paused' && (
            <BookmarkButton
              icon={<StartIcon />}
              label="Возобновить"
              clickedStateBottom={
                <div className={styles.clickable} onClick={onResume}>
                  Да, возобновить
                </div>
              }
              clickedStateTop={<div>Вы уверены?</div>}
              disabled={isResuming}
            />
          )}
        </BookmarkButtonBar>
      )}
    </>
  );
}
